import type { SandboxTarget } from '@kyumo/common';
import {
  AlertCircle,
  Bot,
  CheckCircle,
  ChevronRight,
  Cloud,
  Cog,
  FolderCog,
  FolderOpen,
  Handshake,
  Loader2,
  Plug,
  Sparkles,
  Wrench,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { formatDuration } from '../utilities';
import { getApprovalMethodLabel } from './approval-labels';
import { SandboxRunCodeDisplay } from './SandboxRunCodeDisplay';
import {
  CLOUD_SANDBOX_BADGE_COLOR,
  SANDBOX_TARGET_LABELS,
  SANDBOX_TARGET_TITLES,
} from './sandbox-target-label';
import type { ToolApprovalOutcome, ToolCategory } from './task-detail-types';
import { getCategoryBadgeColor, getCategoryLabel } from './task-detail-utils';

/**
 * カテゴリアイコンを取得
 */
function getCategoryIcon(category?: ToolCategory) {
  switch (category) {
    case 'builtin':
      return <Cog className="w-3 h-3" />;
    case 'manage':
      return <FolderCog className="w-3 h-3" />;
    case 'sandbox':
      return <FolderOpen className="w-3 h-3" />;
    case 'skill':
      return <Sparkles className="w-3 h-3" />;
    case 'subagent':
      return <Bot className="w-3 h-3" />;
    case 'mcp':
      return <Plug className="w-3 h-3" />;
    case 'collaboration':
      return <Handshake className="w-3 h-3" />;
    default:
      return <Wrench className="w-3 h-3" />;
  }
}

/**
 * ステータスアイコンを取得
 */
function getStatusIcon(status?: 'running' | 'completed' | 'failed' | 'cancelled') {
  switch (status) {
    case 'running':
      return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'cancelled':
      return <AlertCircle className="w-4 h-4 text-amber-500" />;
    default:
      return <Wrench className="w-4 h-4 text-muted-foreground" />;
  }
}

/**
 * ツール呼び出しカードコンポーネント（desktop-appと同じ構造）
 */
export function ToolCallCard({
  toolName,
  category,
  status,
  intent,
  args,
  result,
  isError,
  isCancelled,
  approvalOutcome,
  autoApprovedBy,
  durationMs,
  sandboxTarget,
}: {
  toolName: string;
  category: ToolCategory | undefined;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  intent: string | undefined;
  args: unknown;
  result: unknown;
  isError: boolean | undefined;
  isCancelled: boolean | undefined;
  approvalOutcome?: ToolApprovalOutcome | undefined;
  autoApprovedBy?: string | undefined;
  /** ツール本体の実行に要した時間（ミリ秒）。記録がある場合のみ */
  durationMs?: number | undefined;
  /**
   * このタスクがどの実行先で実行されたか。
   *
   * **業務データがどこへ渡ったかの記録である。** 監査で読むとき、ツール 1 件ごとに
   * どこで動いたかが分かる必要がある。記録を持たない古いタスクでは未設定。
   */
  sandboxTarget?: SandboxTarget | undefined;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [showOutput, setShowOutput] = useState(false);

  const isRunning = status === 'running';
  const isFailed = status === 'failed';

  // 実行環境で動いたツールだけ、どの実行先で動いたかをカテゴリバッジで示す。
  // 他のツール（MCP・Web 検索など）はホスト側で動くため実行先の概念がない。
  //
  // **判断はカテゴリではなく実行先の記録で行う。** 撮影やメール送信は遅延ツール
  // （`deferred__execute`）として届き、カテゴリは「ビルトイン」になる。カテゴリで
  // 絞ると、監査で読むべきその 1 件だけが実行先を伴わずに並ぶ
  const isCloudSandbox = sandboxTarget === 'cloud';
  const categoryLabel = sandboxTarget
    ? `${getCategoryLabel(category)}（${SANDBOX_TARGET_LABELS[sandboxTarget]}）`
    : getCategoryLabel(category);

  // intent がある場合は intent を表示、ない場合は「ツールを実行中」を表示
  const displayText = intent || 'ツールを実行中';
  const maxLength = 50;
  const truncatedText =
    displayText.length > maxLength ? `${displayText.slice(0, maxLength)}...` : displayText;

  // 承認方法: 実決定スタンプ（approvalOutcome）優先・覗き見（autoApprovedBy）フォールバック
  const approvalMethod = approvalOutcome
    ? { label: getApprovalMethodLabel(approvalOutcome.source, approvalOutcome.decision) }
    : autoApprovedBy
      ? { label: getApprovalMethodLabel(autoApprovedBy) }
      : undefined;

  return (
    <div className="my-2 rounded-md border border-border bg-card">
      {/* ヘッダー */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 p-2 text-left text-sm hover:bg-muted/50 transition-colors"
      >
        <ChevronRight
          className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`}
        />
        <span
          className={`group/badge inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium transition-all duration-200 ${
            isCloudSandbox ? CLOUD_SANDBOX_BADGE_COLOR : getCategoryBadgeColor(category)
          }`}
          data-testid="tool-category"
          data-sandbox-target={sandboxTarget}
          // **実行先の説明は実行環境を通ったツールにだけ付ける。** タスク単位の
          // 記録があらゆるツールのカードへ渡るのを止めるのは呼び出し側の役目で
          // あり（`MessageCard`）、ここへ届く値は実行環境を通った呼び出しのものに
          // 限られる。絞り直すとカテゴリが「ビルトイン」になる遅延ツール
          // （撮影・メール送信）の実行先が、監査でだけ見えなくなる
          title={sandboxTarget ? SANDBOX_TARGET_TITLES[sandboxTarget] : undefined}
        >
          {isCloudSandbox ? <Cloud className="w-4 h-4 text-sky-500" /> : getCategoryIcon(category)}
          <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 group-hover/badge:max-w-48 group-hover/badge:opacity-100">
            {categoryLabel}
          </span>
        </span>
        <span className={`font-medium text-foreground ${isRunning ? 'animate-pulse' : ''}`}>
          {truncatedText}
        </span>
        {isRunning && <span className="text-xs text-muted-foreground">...</span>}
        {isFailed && <span className="text-xs text-red-400">失敗</span>}
        {isCancelled && <span className="text-xs text-amber-500">キャンセル</span>}
        {/* 承認バッジはステータスアイコンの左隣（右寄せ）。デスクトップと統一 */}
        <span className="ml-auto inline-flex items-center gap-2">
          {/*
            所要時間は右寄せグループの先頭。デスクトップのツールヘッダーと同じ位置で、
            意図テキストのみが切り詰められる既存の挙動を保つ。
            記録が無い場合（本機能の導入前の履歴・実行中）は要素自体を描画しない。
            formatDuration は負値を 0.0秒 へ丸めるため、判定を関数へ委ねずここで弾く。
          */}
          {durationMs !== undefined && Number.isFinite(durationMs) && durationMs >= 0 && (
            <span
              data-testid="tool-duration"
              className="text-xs text-muted-foreground tabular-nums"
            >
              {formatDuration(durationMs)}
            </span>
          )}
          {approvalMethod && (
            <span
              data-testid="tool-approval-method"
              className="rounded-full border border-border px-1.5 py-0.5 text-xs text-muted-foreground"
            >
              {approvalMethod.label}
            </span>
          )}
          {getStatusIcon(status)}
        </span>
      </button>

      {/* 展開時のコンテンツ */}
      {isOpen &&
        (toolName === 'sandbox__runCode' ? (
          /* sandbox__runCode 専用表示 */
          <div className="border-t border-border p-2">
            <SandboxRunCodeDisplay args={args} result={result} />
          </div>
        ) : (
          <>
            {/* ツール入力 */}
            <div className="border-t border-border">
              <button
                type="button"
                onClick={() => setShowInput(!showInput)}
                className="flex w-full items-center gap-1 p-2 text-left hover:bg-muted/30 transition-colors"
              >
                <ChevronRight
                  className={`w-3 h-3 text-muted-foreground transition-transform ${showInput ? 'rotate-90' : ''}`}
                />
                <span className="text-xs text-muted-foreground">ツール: </span>
                <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono font-semibold text-foreground">
                  {toolName}
                </code>
              </button>
              {showInput && args !== undefined && (
                <div className="px-2 pb-2">
                  <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-muted/50 p-2 text-xs text-foreground">
                    {typeof args === 'string' ? args : JSON.stringify(args, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {/* ツール出力 */}
            {result !== undefined && (
              <div className="border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowOutput(!showOutput)}
                  className="flex w-full items-center gap-1 p-2 text-left hover:bg-muted/30 transition-colors"
                >
                  <ChevronRight
                    className={`w-3 h-3 text-muted-foreground transition-transform ${showOutput ? 'rotate-90' : ''}`}
                  />
                  <span className="text-xs text-muted-foreground">
                    {isError ? 'エラー詳細' : isCancelled ? 'キャンセル' : '実行結果'}
                  </span>
                </button>
                {showOutput && (
                  <div className="px-2 pb-2">
                    <pre
                      className={`overflow-x-auto whitespace-pre-wrap break-all rounded p-2 text-xs ${
                        isError
                          ? 'bg-red-950/50 text-red-200'
                          : isCancelled
                            ? 'bg-amber-950/50 text-amber-200'
                            : 'bg-muted/50 text-foreground'
                      }`}
                    >
                      {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </>
        ))}
    </div>
  );
}
